import { ButtonHTMLAttributes, ReactNode } from 'react';
import Spinner from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'on-navy' | 'ghost-on-navy';
type Size = 'xs' | 'sm' | 'md';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  loading?: boolean;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:   'border border-accent bg-accent text-navy hover:brightness-105 disabled:opacity-50',
  secondary: 'border border-navy bg-navy text-white hover:bg-navy-2 disabled:opacity-50',
  ghost:     'border border-line-strong bg-surface text-ink hover:bg-surface-2 disabled:opacity-50',
  danger:    'border border-danger bg-danger text-white hover:brightness-110 disabled:opacity-50',
  // Design `.btn-red`: white with a red outline and text.
  'danger-outline': 'border border-danger-line bg-surface text-danger-ink hover:bg-danger-bg disabled:opacity-50',
  // Design `.btn-d` / `.btn-ghost` on a navy focus panel.
  'on-navy':  'border border-navy-3 bg-navy-2 text-white hover:brightness-110 disabled:opacity-50',
  'ghost-on-navy': 'border border-transparent bg-transparent text-on-navy hover:bg-navy-2 hover:text-white disabled:opacity-50',
};

// Minimum heights keep every button a usable tap target: md meets the 44px
// rule, sm stays visually compact for table-row actions without dropping to the
// ~26px its padding alone would give.
const SIZE_CLASSES: Record<Size, string> = {
  // Design `.mini` tool button.
  xs: 'min-h-[34px] text-[12px] px-[11px] py-0.5 rounded-[10px]',
  // Design `.btn-s`.
  sm: 'min-h-[36px] text-[13px] px-3 py-1 rounded-[10px]',
  md: 'min-h-[44px] text-sm px-4 py-2 rounded-[12px]',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  disabled,
  className = '',
  children,
  ...rest
}: Props) {
  const isDisabled = disabled || loading;
  return (
    <button
      {...rest}
      disabled={isDisabled}
      className={`inline-flex items-center justify-center gap-2 font-semibold transition ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${className}`}>
      {loading ? <Spinner size="sm" /> : (icon ?? null)}
      <span>{children}</span>
    </button>
  );
}
